import React, { useEffect, useState, useRef } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Checkbox } from 'primereact/checkbox'

import { CustomModal, Loading, CustomDataTableHeader } from '../../../../components'
import { showMessageBox } from '../../../../store/actions/system.actions'
import * as AnalistaEntregaHttp from '../../../../services/api/https/registration/analista-entrega.http'

const ModalAssociarVeiculos = ({ show, onClose, analista, selectedTp, dispatch }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [veiculos, setVeiculos] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [processingVehicleId, setProcessingVehicleId] = useState(null)
  const [isProcessingAll, setIsProcessingAll] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    return () => { isMounted.current = false }
  }, [])

  const fetchVeiculos = async () => {
    try {
      const data = await AnalistaEntregaHttp.getVeiculosByTp(selectedTp)
      if (isMounted.current) setVeiculos(data)
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      if (isMounted.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    if (show) fetchVeiculos()
  }, [show, selectedTp]) // eslint-disable-line react-hooks/exhaustive-deps

  const isVehicleAssociatedToCurrentAnalista = (vehicle) => {
    return vehicle.analista_entrega_id === analista.analista_id
  }

  const isVehicleAssociatedToOtherAnalista = (vehicle) => {
    return vehicle.analista_entrega_id && vehicle.analista_entrega_id !== analista.analista_id
  }

  // Derivado: todos os veículos disponíveis (sem analista) estão selecionados
  const availableVehicles = veiculos.filter(v => !isVehicleAssociatedToOtherAnalista(v))
  const selectedVehicles = veiculos.filter(v => isVehicleAssociatedToCurrentAnalista(v))
  const allAvailableSelected = availableVehicles.length > 0 && selectedVehicles.length === availableVehicles.length

  const handleCheckboxChange = async (vehicle) => {
    if (processingVehicleId || isProcessingAll) return

    setProcessingVehicleId(vehicle.vehicle_id)
    try {
      if (isVehicleAssociatedToCurrentAnalista(vehicle)) {
        await AnalistaEntregaHttp.desassociarVeiculo(analista.analista_id, vehicle.vehicle_id)
        setVeiculos(prev =>
          prev.map(v =>
            v.vehicle_id === vehicle.vehicle_id
              ? { ...v, analista_entrega_id: null, analista_name: null }
              : v
          )
        )
      } else {
        await AnalistaEntregaHttp.associarVeiculo(analista.analista_id, vehicle.vehicle_id)
        setVeiculos(prev =>
          prev.map(v =>
            v.vehicle_id === vehicle.vehicle_id
              ? { ...v, analista_entrega_id: analista.analista_id, analista_name: analista.analista_name }
              : v
          )
        )
      }
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      if (isMounted.current) setProcessingVehicleId(null)
    }
  }

  const handleSelectAllChange = async () => {
    if (processingVehicleId || isProcessingAll) return

    setIsProcessingAll(true)
    try {
      if (allAvailableSelected) {
        // Desassociar todos os veículos do TP vinculados a este analista
        await AnalistaEntregaHttp.desassociarTodosVeiculos(analista.analista_id, selectedTp)
        setVeiculos(prev =>
          prev.map(v =>
            isVehicleAssociatedToCurrentAnalista(v)
              ? { ...v, analista_entrega_id: null, analista_name: null }
              : v
          )
        )
      } else {
        // Associar todos os veículos disponíveis (sem analista) ao analista
        await AnalistaEntregaHttp.associarTodosVeiculos(analista.analista_id, selectedTp)
        setVeiculos(prev =>
          prev.map(v =>
            !v.analista_entrega_id
              ? { ...v, analista_entrega_id: analista.analista_id, analista_name: analista.analista_name }
              : v
          )
        )
      }
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      if (isMounted.current) setIsProcessingAll(false)
    }
  }

  const checkboxBody = (rowData) => {
    const isOtherAnalista = isVehicleAssociatedToOtherAnalista(rowData)
    const isCurrentAnalista = isVehicleAssociatedToCurrentAnalista(rowData)
    const isProcessing = processingVehicleId === rowData.vehicle_id

    return (
      <div style={{ textAlign: 'center', position: 'relative' }}>
        {isProcessing ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.2em' }} />
        ) : (
          <Checkbox
            checked={isCurrentAnalista}
            disabled={isOtherAnalista || isProcessingAll}
            onChange={() => handleCheckboxChange(rowData)}
          />
        )}
      </div>
    )
  }

  const analistaBody = (rowData) => {
    return rowData.analista_name || ''
  }

  const rowClassName = (rowData) => {
    if (isVehicleAssociatedToOtherAnalista(rowData)) {
      return { 'p-disabled': true }
    }
    return {}
  }

  return (
    <CustomModal
      header="Associar Veículos a Analista"
      isVisible={show}
      onClose={onClose}
      width="min(70vw, 900px)"
      contentStyle={{ overflowY: 'auto', maxHeight: '70vh' }}
    >
      <p style={{ marginBottom: 8 }}>
        <strong>Analista:</strong> {analista.analista_name}
      </p>
      <p style={{ marginBottom: 12 }}>
        Selecione os Veículos que devem ser associados ao Analista:
      </p>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        {isProcessingAll ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.2em' }} />
        ) : (
          <Checkbox
            inputId="selectAll"
            checked={allAvailableSelected}
            disabled={isLoading || !!processingVehicleId || availableVehicles.length === 0}
            onChange={handleSelectAllChange}
          />
        )}
        <label htmlFor="selectAll" style={{ cursor: 'pointer', userSelect: 'none' }}>
          Selecionar Todos
        </label>
      </div>

      {isLoading ? (
        <Loading size="md" />
      ) : (
        <DataTable
          value={veiculos}
          paginator
          rows={10}
          globalFilter={globalFilter}
          header={<CustomDataTableHeader onChange={setGlobalFilter} />}
          rowClassName={rowClassName}
          emptyMessage="Nenhum veículo encontrado"
        >
          <Column header="#" body={checkboxBody} style={{ textAlign: 'center', width: 60 }} />
          <Column field="vehicle_badge" header="Placa" />
          <Column field="capacity_kg" header="Capacidade KG" />
          <Column header="Analista" body={analistaBody} />
        </DataTable>
      )}
    </CustomModal>
  )
}

export default ModalAssociarVeiculos
